import type { types } from '@bedc/types';

import { geometryUtils } from '../geometryUtils';

import type { ClipPolygon as PcPolygon, ClipRing as PcRing } from './engine';
import type { BBox, Ring, RingSet } from './types';

/**
 * Ring primitives and the conversion bridge to the clipping port.
 *
 * Everything else in this directory builds on these; nothing outside it should
 * need them, with the exception of the containment helpers re-exported by the
 * facade for callers that must match these semantics exactly.
 */

/**
 * Output coordinates are snapped to this grid. Keeps persisted JSON small, keeps
 * repeated cuts from drifting, and makes the explicit-closure check below exact.
 */
const quantum = 1e4;

export const q = (value: number): number => Math.round(value * quantum) / quantum;

export const boundingBox = (ring: Ring): BBox => {
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;

	for (const point of ring) {
		if (point.x < minX) minX = point.x;
		if (point.x > maxX) maxX = point.x;
		if (point.y < minY) minY = point.y;
		if (point.y > maxY) maxY = point.y;
	}

	return { minX, minY, maxX, maxY };
};

/**
 * Cheap prefilter. Disjoint bounding boxes imply genuinely disjoint rings, so a
 * negative here is always safe; touching boxes fall through to the real clip.
 */
export const bboxOverlaps = (a: BBox, b: BBox): boolean =>
	!(a.maxX < b.minX || b.maxX < a.minX || a.maxY < b.minY || b.maxY < a.minY);

export const bboxContains = (box: BBox, point: types.ui.geometry.Point): boolean =>
	point.x >= box.minX && point.x <= box.maxX && point.y >= box.minY && point.y <= box.maxY;

/** Even-odd ray cast. Behaviour for a point exactly on an edge is unspecified. */
export const isPointInRing = (point: types.ui.geometry.Point, ring: Ring): boolean => {
	let inside = false;

	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
		const a = ring[i];
		const b = ring[j];

		if (!a || !b) continue;

		const straddles = a.y > point.y !== b.y > point.y;

		if (straddles && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x) {
			inside = !inside;
		}
	}

	return inside;
};

export const countDistinctPoints = (ring: Ring): number => {
	const seen = new Set<string>();

	for (const point of ring) {
		seen.add(`${point.x},${point.y}`);
	}

	return seen.size;
};

export const toPcRing = (ring: Ring): PcRing => ring.map((point) => [point.x, point.y]);

/**
 * Convert a clipping-library ring back to our shape, quantised and EXPLICITLY
 * closed.
 *
 * The closure matters: `geometryUtils.isPathClosed` compares first and last with
 * strict `===`, so a ring that is a floating-point hair from closed measures an
 * area of 0. The last point therefore reuses the first point's exact values
 * rather than being quantised independently.
 *
 * Returns `null` for rings that are degenerate after quantisation.
 */
export const fromPcRing = (pcRing: PcRing): Ring | null => {
	const points: types.ui.geometry.Point[] = pcRing.map(([x, y]) => ({ x: q(x), y: q(y) }));

	const first = points[0];
	if (!first) return null;

	const last = points[points.length - 1];
	const alreadyClosed = last !== undefined && last.x === first.x && last.y === first.y;
	const ring = alreadyClosed ? points : [...points, { x: first.x, y: first.y }];

	// A closed ring needs 3 distinct corners, so 4 entries once the first repeats.
	if (ring.length < 4 || countDistinctPoints(ring) < 3) return null;

	return ring;
};

export const toPcPolygon = (set: RingSet): PcPolygon => [
	toPcRing(set.outer),
	...set.holes.map((hole) => toPcRing(hole)),
];

/**
 * Pair every outer ring with the layer's openings.
 *
 * Openings are NOT attributed to a single parent. A user can draw one opening
 * across two separate areas of the same layer, and it must reduce BOTH — an
 * earlier version picked one parent by testing the opening's first vertex, so a
 * spanning opening subtracted from one area and was left rendered on top of the
 * other. Every consumer clips openings to the ring it is working on, so an
 * opening that does not overlap a given ring contributes nothing to it and the
 * pairing needs no containment test at all.
 *
 * CONTRACT: when `cutouts` is absent or empty this returns exactly
 * `paths.map((outer) => ({ outer, holes: [] }))` — same order, same length, the
 * same `outer` object references, and no filtering of degenerate, open or
 * single-point rings. Measurement byte-identity for uncut layers depends on it,
 * so the early return happens before any geometry runs.
 */
export const groupRings = (paths: Ring[], cutouts?: Ring[]): RingSet[] => {
	if (!cutouts || cutouts.length === 0) {
		return paths.map((outer) => ({ outer, holes: [] }));
	}

	return paths.map((outer) => ({ outer, holes: cutouts }));
};

export const pcRingArea = (pcRing: PcRing): number => {
	let total = 0;

	for (let i = 0; i < pcRing.length; i++) {
		const a = pcRing[i];
		const b = pcRing[(i + 1) % pcRing.length];
		if (!a || !b) continue;
		total += a[0] * b[1] - b[0] * a[1];
	}

	return Math.abs(total / 2);
};

/** Area of a clipped MultiPolygon: outer rings minus their inner rings. */
export const multiPolygonArea = (polygons: PcPolygon[]): number =>
	polygons.reduce((total, polygon) => {
		const [ring, ...inner] = polygon;
		if (!ring) return total;

		return (
			total + pcRingArea(ring) - inner.reduce((holes2, hole) => holes2 + pcRingArea(hole), 0)
		);
	}, 0);

/** Summed segment length of one ring, closed or not. */
export const ringLength = (ring: Ring): number => {
	const points = geometryUtils.pathToFlattenedPoints(ring);
	if (!points || points.length < 4) return 0;

	let total = 0;

	for (let i = 0; i < points.length - 2; i += 2) {
		const [p1, p2, p3, p4] = [points[i], points[i + 1], points[i + 2], points[i + 3]];
		if (p1 === undefined || p2 === undefined || p3 === undefined || p4 === undefined) continue;
		total += geometryUtils.calculatePixelDistance([p1, p2, p3, p4]);
	}

	return total;
};
